import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Play,
  X,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
  Hand,
  Zap,
  Pause,
  HandMetal,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import { Eyebrow } from '@/components/ui/eyebrow';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import {
  useJob,
  useStartAutomation,
  useCancelAutomation,
  useConcluirPrestacao,
  useFasesCatalog,
  type JobSnapshot,
  type FaseStatus,
  type JobStatus,
} from '@/lib/hooks/prestacoes';

export const Route = createFileRoute('/_app/prestacao-live/$id')({
  component: PrestacaoLivePage,
});

const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  QUEUED: 'na fila',
  RUNNING: 'executando',
  WAITING_HUMAN: 'aguardando você',
  DONE: 'concluída',
  FAILED: 'falhou',
};

const JOB_STATUS_TONE: Record<JobStatus, 'mint' | 'warn' | 'pass' | 'block' | 'default'> = {
  QUEUED: 'default',
  RUNNING: 'mint',
  WAITING_HUMAN: 'warn',
  DONE: 'pass',
  FAILED: 'block',
};

function PrestacaoLivePage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const workspaceId = user?.effectiveWorkspaceId ?? null;

  const fasesQ = useFasesCatalog(workspaceId);
  const jobQ = useJob(workspaceId, id);
  const startMut = useStartAutomation(workspaceId);
  const cancelMut = useCancelAutomation(workspaceId);
  const concluirMut = useConcluirPrestacao(workspaceId);

  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmConcluir, setConfirmConcluir] = useState(false);
  const [paused, setPaused] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  const job = jobQ.data && 'status' in jobQ.data ? (jobQ.data as JobSnapshot) : null;
  const fasesCatalog = fasesQ.data?.items ?? [];

  async function handleStart() {
    try {
      const r = await startMut.mutateAsync(id);
      toast.success(`Automação iniciada${r.liveViewUrl ? ' — live view disponível' : ''}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao iniciar.');
    }
  }

  async function handleCancel() {
    try {
      await cancelMut.mutateAsync(id);
      toast.success('Automação cancelada.');
      setConfirmCancel(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cancelar.');
    }
  }

  async function handleConcluir() {
    try {
      await concluirMut.mutateAsync(id);
      toast.success('Prestação concluída.');
      setConfirmConcluir(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao concluir.');
    }
  }

  const doneFases = job?.fases.filter((f) => f.status === 'DONE').length ?? 0;
  const totalFases = fasesCatalog.length;
  const progressPct = totalFases > 0 ? (doneFases / totalFases) * 100 : 0;

  const isAutomating = job?.status === 'RUNNING' || job?.status === 'QUEUED';

  const thinkingText = (() => {
    if (job?.status !== 'RUNNING') return null;
    const currentDef = job.currentFase != null
      ? fasesCatalog.find((f) => f.fase === job.currentFase)
      : null;
    const hasStartedLog = job.fases.some(
      (f) => f.fase === job.currentFase && f.status === 'STARTED',
    );
    if (currentDef && hasStartedLog) {
      return `Aguardando resposta do portal Suzano para a fase ${currentDef.nome}…`;
    }
    return 'Sincronizando estado…';
  })();

  return (
    <div className="relative flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate({ to: '/prestacoes' })}
            className="flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg mb-2"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
            Voltar pra lista
          </button>
          <Eyebrow>Automação</Eyebrow>
          <h1 className="font-display text-[28px] md:text-[36px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Acompanhamento em tempo real
          </h1>
          {isAutomating && totalFases > 0 && (
            <p className="font-mono text-[11px] text-fg-muted mt-1 tracking-[0.04em]">
              FASE {doneFases} DE {totalFases}
            </p>
          )}
          <p className="text-sm text-fg-muted mt-1.5 max-w-2xl">
            Prestação{' '}
            <span className="font-mono text-fg-muted">{id.slice(0, 8)}…</span> · cada uma das 16
            fases roda no portal Suzano e marca aqui quando termina. O clique final em "✓ Concluir"
            é <strong className="text-fg">sempre humano</strong>.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {job && (
            <Pill tone={JOB_STATUS_TONE[job.status]} dot>
              {JOB_STATUS_LABEL[job.status]}
            </Pill>
          )}

          {!job && (
            <Button variant="primary" size="lg" onClick={handleStart} disabled={startMut.isPending}>
              {startMut.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Iniciando…
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" strokeWidth={1.8} />
                  Iniciar automação
                </>
              )}
            </Button>
          )}

          {isAutomating && (
            <>
              {/* Pausar — estado local, sem API */}
              <Button
                variant="ghost"
                size="lg"
                onClick={() => {
                  if (paused) {
                    setPaused(false);
                    toast.success('Retomando — a IA está de volta.');
                  } else {
                    setPaused(true);
                    toast('Pausa solicitada — o portal seguirá até o próximo ponto de retomada.');
                  }
                }}
                aria-pressed={paused}
              >
                {paused ? (
                  <>
                    <RotateCcw className="h-4 w-4" strokeWidth={1.8} />
                    Retomar
                  </>
                ) : (
                  <>
                    <Pause className="h-4 w-4" strokeWidth={1.8} />
                    Pausar
                  </>
                )}
              </Button>

              {/* Modo manual — estado local, sem API */}
              <Button
                variant="ghost"
                size="lg"
                onClick={() => {
                  if (manualMode) {
                    setManualMode(false);
                    toast.success('Controle devolvido. A IA retomará a fase atual.');
                  } else {
                    setManualMode(true);
                    toast('Modo manual ativado — você controla o browser agora.');
                  }
                }}
                className={cn(manualMode ? 'text-warn bg-warn-soft/40' : 'text-warn')}
                aria-pressed={manualMode}
              >
                <HandMetal className="h-4 w-4" strokeWidth={1.8} />
                {manualMode ? 'Modo manual ON' : 'Modo manual'}
              </Button>

              {/* Cancelar */}
              <Button
                variant="ghost"
                size="lg"
                onClick={() => setConfirmCancel(true)}
                className="text-block hover:bg-block-soft"
              >
                <X className="h-4 w-4" strokeWidth={1.8} />
                Cancelar
              </Button>
            </>
          )}

          {job && (job.status === 'FAILED' || job.status === 'DONE') && (
            <Button variant="primary" size="lg" onClick={handleStart} disabled={startMut.isPending}>
              <Zap className="h-4 w-4" strokeWidth={1.8} />
              Tentar novamente
            </Button>
          )}
        </div>
      </header>

      {/* Estado B — WAITING_HUMAN: handoff banner fullscreen-ish */}
      {job?.status === 'WAITING_HUMAN' && (
        <div className="handoff-banner" role="status" aria-live="assertive">
          <Eyebrow className="text-on-mint/80">FINAL HUMANO</Eyebrow>
          <h2>Revise no portal e clique &ldquo;✓ Concluir&rdquo;</h2>
          <p>
            A automação preencheu as 16 fases. O clique final é sempre humano por
            responsabilidade legal.
          </p>
          <div className="actions">
            <Button variant="ink" onClick={() => setConfirmConcluir(true)}>
              <Hand className="h-4 w-4" strokeWidth={1.8} />
              Já concluí
            </Button>
            {job.liveViewUrl && (
              <Button variant="ghost" onClick={() => window.open(job.liveViewUrl ?? undefined, '_blank')}>
                Abrir portal
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-5">
        {/* LIVE VIEW */}
        <Card variant="default" className="overflow-hidden p-0 min-h-[500px] relative flex flex-col">
          {/* Estado C — manual-badge */}
          {manualMode && (
            <div className="manual-badge" aria-label="Modo manual ativo">
              MODO MANUAL
            </div>
          )}

          {/* Overlay de pausa */}
          {paused && (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-ink/60 backdrop-blur-sm"
              role="status"
              aria-live="polite"
            >
              <Pause className="h-10 w-10 text-on-ink opacity-80" strokeWidth={1.6} />
              <p className="text-on-ink text-base font-medium">
                Pausado. Retomar quando quiser.
              </p>
              <Button
                variant="primary"
                onClick={() => {
                  setPaused(false);
                  toast.success('Retomando — a IA está de volta.');
                }}
              >
                <RotateCcw className="h-4 w-4" strokeWidth={1.8} />
                Retomar
              </Button>
            </div>
          )}

          {job?.liveViewUrl ? (
            <iframe
              src={job.liveViewUrl}
              title="Live view do portal Suzano"
              className={cn(
                'w-full flex-1 min-h-[560px] border-0',
                manualMode ? 'pointer-events-auto' : 'pointer-events-none',
              )}
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
          ) : (
            <div className="grid place-items-center flex-1 min-h-[560px] p-8">
              <div className="text-center max-w-md">
                <div className="grid h-14 w-14 place-items-center rounded-3 bg-card-soft text-fg-muted mx-auto mb-3">
                  <Zap className="h-7 w-7" strokeWidth={1.8} />
                </div>
                <h3 className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg">
                  {job ? 'Live view não disponível' : 'Pronto pra começar'}
                </h3>
                <p className="text-sm text-fg-muted mt-2">
                  {job
                    ? 'A sessão de browser não retornou um live view (provider não configurado ou modo simulação).'
                    : 'Clica em "Iniciar automação" pra abrir uma sessão de browser remoto e preencher as 16 fases automaticamente.'}
                </p>
              </div>
            </div>
          )}

          {/* CTA "Devolver à IA" quando no modo manual */}
          {manualMode && (
            <div className="border-t border-card-stroke p-3">
              <Button
                variant="primary"
                className="w-full bg-warn text-on-ink hover:opacity-90"
                onClick={() => {
                  setManualMode(false);
                  toast.success('Controle devolvido. A IA retomará a fase atual.');
                }}
              >
                <RotateCcw className="h-4 w-4" strokeWidth={1.8} />
                Devolver à IA
              </Button>
            </div>
          )}
        </Card>

        {/* FASES */}
        <Card variant="default" className="p-0 overflow-hidden flex flex-col">
          <header className="border-b border-card-stroke px-4 py-3">
            <Eyebrow>Progresso</Eyebrow>
            <h3 className="font-display text-[16px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
              Fases do portal Suzano
            </h3>
            {/* Progress bar — visível quando RUNNING ou QUEUED */}
            {isAutomating && (
              <div className="progress info mt-2.5" role="progressbar" aria-valuenow={Math.round(progressPct)} aria-valuemin={0} aria-valuemax={100}>
                <div className="bar" style={{ width: `${progressPct}%` }} />
              </div>
            )}
          </header>

          <ol className="divide-y divide-card-stroke flex-1 overflow-y-auto">
            {fasesCatalog.map((def) => {
              const log = job?.fases.find((f) => f.fase === def.fase);
              const isCurrent = job?.currentFase === def.fase && job?.status === 'RUNNING';
              const status: FaseStatus | 'PENDING' = log?.status ?? 'PENDING';
              return (
                <li
                  key={def.fase}
                  className={cn(
                    'flex items-center gap-3 px-4 py-2.5 transition-colors',
                    isCurrent && 'bg-mint-soft/50',
                  )}
                >
                  <div
                    className={cn(
                      'grid h-7 w-7 place-items-center rounded-full text-[11px] font-mono font-semibold shrink-0',
                      status === 'DONE' && 'bg-mint-deep text-on-mint',
                      status === 'FAILED' && 'bg-block text-on-ink',
                      status === 'STARTED' && 'bg-mint-soft text-mint-deep animate-pulse',
                      status === 'PENDING' && 'bg-card-soft text-fg-muted',
                      status === 'HEALED' && 'bg-lilac-soft text-lilac-deep',
                    )}
                  >
                    {status === 'DONE' ? (
                      <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
                    ) : status === 'FAILED' ? (
                      <AlertCircle className="h-3.5 w-3.5" strokeWidth={2} />
                    ) : status === 'STARTED' ? (
                      /* Estado C — modo manual substitui spin no item ativo */
                      manualMode && isCurrent ? (
                        <Hand className="h-3.5 w-3.5" strokeWidth={2} />
                      ) : (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                      )
                    ) : (
                      def.fase
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-fg truncate">{def.nome}</p>
                    {isCurrent && manualMode && (
                      <p className="text-[11px] text-warn mt-0.5 font-mono tracking-wide">
                        ⏸ Modo manual
                      </p>
                    )}
                    {log?.errorMessage && (
                      <p className="text-[11px] text-warn mt-0.5 truncate">{log.errorMessage}</p>
                    )}
                  </div>
                  {log?.finishedAt && status === 'DONE' && (
                    <Clock className="h-3.5 w-3.5 text-fg-dim" strokeWidth={1.8} />
                  )}
                </li>
              );
            })}
          </ol>

          {/* Thinking stream — visível apenas quando RUNNING */}
          {job?.status === 'RUNNING' && thinkingText && (
            <div className="border-t border-card-stroke p-4">
              <div className="thinking">
                <div className="think-label">PENSANDO</div>
                {thinkingText}
              </div>
            </div>
          )}

          {/* Rodapé — custo IA */}
          <div className="border-t border-card-stroke px-4 py-2.5">
            <p className="font-mono text-[11px] text-fg-dim tracking-[0.02em]">
              Custo IA: R$ —
            </p>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        tone="danger"
        title="Cancelar automação?"
        description="A sessão de browser será fechada e a prestação voltará pra estado CANCELADA. Você poderá iniciar de novo depois."
        confirmLabel="Cancelar"
        loading={cancelMut.isPending}
        onConfirm={handleCancel}
      />

      <ConfirmDialog
        open={confirmConcluir}
        onOpenChange={setConfirmConcluir}
        title="Marcar como concluída?"
        description="Confirma que você JÁ clicou em '✓ Concluir' no portal Suzano? Esta ação não pode ser desfeita."
        confirmLabel="Sim, está concluída"
        loading={concluirMut.isPending}
        onConfirm={handleConcluir}
      />
    </div>
  );
}
